import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const FindSessionByEmail = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load font and inject keyframes
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&family=Poppins:wght@400;500;600;700&family=Quicksand:wght@400;500;600;700&display=swap';
    document.head.appendChild(fontLink);
    
    const style = document.createElement('style');
    style.textContent = `
      @keyframes titleWave {
        0% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
        100% { transform: translateY(0); }
      }
      @keyframes gradientShift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      @keyframes floatBlob {
        0% { transform: translateY(0) scale(1); }
        50% { transform: translateY(-20px) scale(1.08); }
        100% { transform: translateY(0) scale(1); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(fontLink)) {
        document.head.removeChild(fontLink);
      }
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Email',
        text: 'Please enter a valid email address',
        confirmButtonColor: '#ccb4d6'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://api.laviopaperie.com/api/photo-session/find-by-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to find session');
      }

      if (data.success && data.session && data.session.indukname) {
        setLoading(false);
        
        // Navigate to camera-results with the found sessionId
        navigate(`/camera-results/${data.session.indukname}`);
      } else {
        throw new Error('Session not found');
      }

    } catch (error) {
      setLoading(false);
      console.error('Error finding session:', error);
      Swal.fire({
        icon: 'error',
        title: 'Session Not Found',
        text: error.message || 'No session found with this email for today. Please check your email or try again later.',
        confirmButtonColor: '#ccb4d6'
      });
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100vw',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #fbf8ff 0%, #f4fff9 50%, #fffaf5 100%)',
      backgroundSize: '200% 200%',
      animation: 'gradientShift 16s ease infinite',
      position: 'relative',
      overflow: 'hidden',
      padding: '20px'
    }}>
      {/* Animated blobs */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        <div style={{ position: 'absolute', top: '-100px', left: '-80px', width: '400px', height: '400px', background: 'radial-gradient(circle at 30% 30%, rgba(255,182,193,0.6), rgba(255,182,193,0.1), transparent)', filter: 'blur(25px)', animation: 'floatBlob 15s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '-120px', right: '-90px', width: '450px', height: '450px', background: 'radial-gradient(circle at 70% 70%, rgba(173,216,230,0.6), rgba(173,216,230,0.1), transparent)', filter: 'blur(28px)', animation: 'floatBlob 18s ease-in-out infinite' }} />
      </div>

      {/* Main Card */}
      <div style={{
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(10px)',
        borderRadius: '24px',
        padding: '48px 40px',
        maxWidth: '600px',
        width: '100%',
        zIndex: 1,
        position: 'relative',
        boxShadow: '0 10px 40px rgba(204, 180, 214, 0.3)',
        border: '1px solid rgba(204, 180, 214, 0.3)'
      }}>
        {/* Title */}
        <div style={{
          fontFamily: `'Great Vibes', cursive`,
          fontSize: '48px',
          lineHeight: 1.1,
          letterSpacing: '1px',
          color: '#8f72a6',
          textShadow: '0 1px 0 rgba(255,255,255,0.6)',
          textAlign: 'center',
          marginBottom: '24px'
        }}>
          <div style={{ whiteSpace: 'pre' }}>
            {Array.from('Edit and save').map((ch, i) => (
              <span
                key={i}
                style={{
                  display: 'inline-block',
                  animation: 'titleWave 2.2s ease-in-out infinite',
                  animationDelay: `${i * 0.06}s`,
                  willChange: 'transform'
                }}
              >
                {ch === ' ' ? '\u00A0' : ch}
              </span>
            ))}
          </div>
          
          <div>
            {Array.from('your pic').map((ch, i) => (
              <span
                key={`pic-${i}`}
                style={{
                  display: 'inline-block',
                  animation: 'titleWave 2.2s ease-in-out infinite',
                  animationDelay: `${(i + 22) * 0.06}s`,
                  willChange: 'transform'
                }}
              >
                {ch === ' ' ? '\u00A0' : ch}
              </span>
            ))}
          </div>
        </div>

        {/* Message */}
        <p style={{
          fontFamily: "'Poppins', sans-serif",
          fontSize: '18px',
          color: '#6c757d',
          textAlign: 'center',
          marginBottom: '32px',
          lineHeight: 1.6
        }}>
          Enter your email to find your photo session from today
        </p>

        {/* Email Form */}
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontFamily: "'Poppins', sans-serif",
              fontSize: '16px',
              fontWeight: 600,
              color: '#7c5fa6',
              marginBottom: '12px'
            }}>
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px 18px',
                fontSize: '16px',
                borderRadius: '12px',
                border: '2px solid #ccb4d6',
                outline: 'none',
                fontFamily: "'Poppins', sans-serif",
                background: '#f8f4ff',
                color: '#7c5fa6',
                transition: 'all 0.3s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#a28ad6';
                e.target.style.boxShadow = '0 0 0 3px rgba(162, 138, 214, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#ccb4d6';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px 0',
              borderRadius: '999px',
              border: 'none',
              background: loading 
                ? 'linear-gradient(90deg, #ccb4d6 0%, #b4e7f8 100%)' 
                : 'linear-gradient(90deg, #a28ad6 0%, #f8e1f4 100%)',
              color: '#fff',
              fontSize: '18px',
              fontWeight: 700,
              letterSpacing: '1px',
              boxShadow: '0 4px 16px 0 rgba(204,180,214,0.3)',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s',
              fontFamily: "'Poppins', sans-serif"
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'scale(1.02)';
                e.target.style.boxShadow = '0 6px 20px 0 rgba(204,180,214,0.4)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 4px 16px 0 rgba(204,180,214,0.3)';
            }}
          >
            {loading ? 'Searching...' : 'Find Session'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FindSessionByEmail;

